
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'About Cape Town Professional Painters | 15+ Years Experience | Licensed Painting Contractors';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about Cape Town\'s most trusted painting contractors. Over 15 years of experience serving Atlantic Beach Estate, Big Bay, Bloubergstrand, Bothasig, Camps Bay. Professional and reliable.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'about Cape Town painters, professional painting contractors, licensed painters Cape Town, painting company history');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Cape Town Professional Painters",
      "description": "Learn about Cape Town's most trusted painting contractors with over 15 years of experience.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Professional Painters",
        "foundingDate": "2008",
        "description": "Professional painting contractors serving Cape Town for over 15 years",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Cape Town",
          "addressRegion": "Western Cape",
          "addressCountry": "ZA"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('AboutPage')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3b8bf23507885e190ec0b0671e00cd29.jpg"
            alt="Professional painting team in Cape Town"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              About <span className="text-yellow-400">Cape Town Professional Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Over 15 years of excellence in residential and commercial painting across Cape Town's most prestigious neighborhoods
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">Our Story</h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  Cape Town Painters is a premium platform that connects discerning property owners with the finest painting contractors in Cape Town. We understand that finding the right painter for your valuable property can be challenging, which is why we've created a curated network of only the most skilled and reliable professionals in the industry.
                </p>
                <p>
                  Our platform allows you to request up to 5 detailed quotes from our vetted painting contractors, giving you the power to compare services, expertise, and offerings. Alternatively, if you prefer a streamlined approach, we can connect you with our single best-matched contractor based on your specific requirements and project needs.
                </p>
                <p>
                  We don't compete on price – we compete on quality. Our painting contractors are carefully selected based on their expertise, reputation, and commitment to excellence. From luxury coastal estates in Camps Bay to family homes in Bothasig, our network covers all of Cape Town's most sought-after areas with specialists who understand the unique challenges of each location.
                </p>
                <p>
                  Whether you need marine-grade protection for your Bloubergstrand beachfront property, wind-resistant coatings for Big Bay homes, or premium finishes for Atlantic Beach Estate villas, our platform ensures you're connected with contractors who have the specific expertise your project demands. Quality is never compromised – it's our foundation.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <img
                src="https://readdy.ai/api/search-image?query=Premium%20painting%20contractors%20network%20platform%2C%20professional%20painters%20working%20on%20luxury%20Cape%20Town%20properties%2C%20quality%20painting%20services%2C%20multiple%20contractors%20collaboration%2C%20professional%20service%20platform%2C%20realistic%20photography%20with%20Cape%20Town%20cityscape&width=600&height=400&seq=platform-network&orientation=landscape"
                alt="Cape Town Painters premium contractor network"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-black mb-4">Our Mission</h3>
                <p className="text-gray-700 leading-relaxed">
                  To connect property owners with Cape Town's most skilled painting contractors, ensuring every project receives the premium quality and professional service it deserves. We believe in quality over cost, excellence over compromise.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Why Choose Cape Town Painters</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine years of experience with modern techniques to deliver exceptional results
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-award-line',
                title: 'Expert Craftsmanship',
                description: 'Our skilled painters bring years of experience and attention to detail to every project'
              },
              {
                icon: 'ri-palette-line',
                title: 'Premium Materials',
                description: 'We use only the highest quality paints and materials for long-lasting, beautiful results'
              },
              {
                icon: 'ri-time-line',
                title: 'Timely Completion',
                description: 'We respect your schedule and complete projects on time without compromising quality'
              },
              {
                icon: 'ri-shield-check-line',
                title: '*Fully Insured',
                description: 'Complete insurance coverage gives you peace of mind throughout the painting process. *Check with your chosen painter what insurance they offer.'
              },
              {
                icon: 'ri-customer-service-line',
                title: 'Excellent Service',
                description: 'From initial consultation to project completion, we provide outstanding customer service'
              },
              {
                icon: 'ri-price-tag-3-line',
                title: 'Fair Pricing',
                description: 'Transparent, competitive pricing with no hidden costs or surprise charges'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-yellow-400 rounded-lg flex items-center justify-center mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${feature.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">The Cape Town Painters Advantage</h2>
              <div className="space-y-6">
                {[
                  {
                    icon: 'ri-check-double-line',
                    title: '*Quality Guarantee',
                    description: 'Our painters stand behind their work with comprehensive warranties on all painting services. *Check with your chosen painter what warranties they offer.'
                  },
                  {
                    icon: 'ri-leaf-line',
                    title: 'Eco-Friendly Options',
                    description: 'Low-VOC and environmentally safe paint options available for health-conscious clients'
                  },
                  {
                    icon: 'ri-tools-line',
                    title: 'Professional Equipment',
                    description: 'State-of-the-art tools and equipment ensure efficient, high-quality results'
                  },
                  {
                    icon: 'ri-team-line',
                    title: 'Experienced Team',
                    description: 'Our painters are trained professionals with extensive experience in all painting techniques'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#ffff00'}}>
                      <i className={`${benefit.icon} text-black text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20interior%20wall%20with%20brushes%20and%20rollers%2C%20modern%20residential%20setting%2C%20clean%20workspace%20with%20drop%20cloths%20and%20professional%20equipment%2C%20natural%20lighting%2C%20high%20quality%20craftsmanship%20demonstration&width=600&height=500&seq=about-team&orientation=landscape"
                alt="Professional painting team at work"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here’s what our satisfied customers have to say about our work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Jennifer Adams',
                location: 'Camps Bay',
                rating: 5,
                text: 'Absolutely exceptional service from start to finish. The team transformed our entire villa with such attention to detail. The marine‑grade paint they recommended has held up beautifully against the coastal conditions. Highly recommend!',
                project: 'Luxury Villa Exterior'
              },
              {
                name: 'Robert Mitchell',
                location: 'Atlantic Beach Estate',
                rating: 5,
                text: 'Professional, punctual, and perfectionist. Michael and his team exceeded our expectations. The color consultation was invaluable, and the final result is stunning. Our property value has definitely increased.',
                project: 'Complete Interior Renovation'
              },
              {
                name: 'Maria Santos',
                location: 'Bloubergstrand',
                rating: 5,
                text: 'We needed our beachfront property painted urgently before a family event. The team worked efficiently without compromising quality. The wind‑resistant coating they used is perfect for our location.',
                project: 'Urgent Exterior Refresh'
              },
              {
                name: 'David Thompson',
                location: 'Big Bay',
                rating: 5,
                text: 'Outstanding commercial painting service for our office complex. Minimal disruption to our business operations, and the quality is top‑notch. The team was professional and courteous throughout.',
                project: 'Commercial Office Complex'
              },
              {
                name: 'Sarah Johnson',
                location: 'Bothasig',
                rating: 5,
                text: 'From quote to completion, everything was handled professionally. The team protected all our furniture and landscaping perfectly. The interior looks amazing, and the warranty gives us peace of mind.',
                project: 'Family Home Interior'
              },
              {
                name: 'Mark Williams',
                location: 'Sea Point',
                rating: 5,
                text: 'Incredible transformation of our apartment. The team worked around our schedule and completed everything on time. The quality of work and materials used is evident in the beautiful finish.',
                project: 'Apartment Renovation'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <h4 className="font-bold text-black">{testimonial.name}</h4>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                  <p className="text-sm text-yellow-600 font-semibold">{testimonial.project}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-4">
            Ready to Transform Your Property?
          </h2>
          <p className="text-xl text-black/80 mb-8">
            Join thousands of satisfied customers who have trusted us with their painting projects. Get your free quote today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
